import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'

import { LetterheadListFragment } from '../../../types/graphql'
import { useLetterheadsQuery } from '../../fetch/letterheads'

const getOptionFromLetterhead = (letterhead: LetterheadListFragment) => {
  return {
    label: letterhead.name,
    value: letterhead,
  }
}

type Props = {
  caption?: string
  isClearable?: boolean
  isRequired?: boolean
  label?: string
  onValueChange: (value: number | null | undefined) => void
  value: number | null | undefined
  width?: React.CSSProperties['width']
  placeholder?: string
}

const LetterheadSelect = ({
  caption,
  isClearable,
  isRequired,
  label,
  onValueChange,
  value,
  width,
  placeholder = 'Select a letterhead',
}: Props) => {
  const { data, isLoading: isLoadingOptions } = useLetterheadsQuery()
  const letterheads = useMemo(() => data?.letterheads ?? [], [data])

  const options: SelectOption<LetterheadListFragment>[] = useMemo(
    () => letterheads.map(getOptionFromLetterhead),
    [letterheads]
  )

  const selectedOption = useMemo(() => {
    if (value) {
      const letterhead = letterheads.find((lh) => lh.id === value)
      return letterhead ? getOptionFromLetterhead(letterhead) : undefined
    }
    return undefined
  }, [value, letterheads])

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<LetterheadListFragment> | undefined) => {
      onValueChange(option?.value?.id ?? null)
    },
    [onValueChange]
  )

  const getOptionKey = useCallback((option: SelectOption<LetterheadListFragment>) => {
    return option.value.id
  }, [])

  return (
    <Select
      caption={caption}
      getOptionKey={getOptionKey}
      isClearable={isClearable}
      isLoading={isLoadingOptions}
      isRequired={isRequired}
      label={label}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder={placeholder}
      selectedOption={selectedOption}
      width={width}
    />
  )
}

export default React.memo(LetterheadSelect)
